import { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { checkAuthorization } from "@/common/auth";
import {
  usePatchAdminUserMutation,
  useDeleteAdminUserCredentialsMutation,
} from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { HGAdminUser, HGAdminUserPatch } from "@/common/types";
import { Avatar, AvatarImage } from "../avatar";
import { Badge } from "../badge";
import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { Input } from "../input";
import { Switch } from "../switch";
import { TableCell, TableRow } from "../table";
import AdminPasswordDialog from "./AdminPasswordDialog";
import AdminUserDeleteDialog from "./AdminUserDeleteDialog";

export default function AdminUserRow({
  user,
  onError,
}: {
  user: HGAdminUser;
  onError: (message: string | null) => void;
}) {
  const me = useAppSelector(selectMe);
  const isSelf = user.id === me?.id;
  const [patch, { isLoading: isPatching }] = usePatchAdminUserMutation();
  const [removeCredentials] = useDeleteAdminUserCredentialsMutation();
  const [collectionSlots, setCollectionSlots] = useState(
    String(user.collection_slots),
  );
  const [pinnedSlots, setPinnedSlots] = useState(String(user.pinned_slots));
  // Blank is a value here, not a missing one: it means "not in the built-in
  // list", which is why this field commits null where the slots ignore NaN.
  const [builtinRank, setBuiltinRank] = useState(
    user.builtin_rank == null ? "" : String(user.builtin_rank),
  );
  const [passwordDialog, setPasswordDialog] = useState<
    "reset" | "create" | null
  >(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Same resolution the API applies for everyone else (get_profile): the
  // user's own override wins over what the identity provider supplied.
  const name = user.display_name || user.identities.find((i) => i.name)?.name;
  const email = user.identities.find((i) => i.email)?.email;
  const picture = user.has_avatar
    ? `/api/user/${user.id}/avatar`
    : user.identities.find((i) => i.picture)?.picture;

  const applyPatch = async (fields: HGAdminUserPatch) => {
    onError(null);
    try {
      await patch({ userID: user.id, patch: fields }).unwrap();
      if (isSelf) await checkAuthorization();
    } catch (e) {
      onError(
        getApiErrorDetail(e, "Could not update that user. Please try again."),
      );
    }
  };

  const commitSlots = () => {
    const fields: { collection_slots?: number; pinned_slots?: number } = {};
    const parsedCollections = parseInt(collectionSlots, 10);
    const parsedPinned = parseInt(pinnedSlots, 10);
    if (
      !isNaN(parsedCollections) &&
      parsedCollections !== user.collection_slots
    ) {
      fields.collection_slots = parsedCollections;
    }
    if (!isNaN(parsedPinned) && parsedPinned !== user.pinned_slots) {
      fields.pinned_slots = parsedPinned;
    }
    if (Object.keys(fields).length > 0) applyPatch(fields);
  };

  const commitBuiltinRank = () => {
    const trimmed = builtinRank.trim();
    const parsed = trimmed === "" ? null : parseInt(trimmed, 10);
    if (parsed !== null && isNaN(parsed)) {
      // Not a number and not empty: put the stored value back rather than
      // guess at what was meant.
      setBuiltinRank(user.builtin_rank == null ? "" : String(user.builtin_rank));
      return;
    }
    if (parsed !== user.builtin_rank) applyPatch({ builtin_rank: parsed });
  };

  const handleRemoveCredentials = async () => {
    onError(null);
    try {
      await removeCredentials(user.id).unwrap();
    } catch (e) {
      onError(getApiErrorDetail(e, "Could not remove that password login."));
    }
  };

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6">
            <AvatarImage src={picture || ""} alt={name || user.id} />
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium">{name || user.id}</span>
            {email && (
              <span className="text-xs text-muted-foreground">{email}</span>
            )}
            <div className="flex gap-1 mt-0.5">
              {user.identities.map((identity) => (
                <Badge key={identity.source} variant="outline">
                  {identity.source}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>{user.username || "-"}</TableCell>
      <TableCell>{user.nickname || "-"}</TableCell>
      <TableCell>
        <Switch
          checked={user.is_admin}
          disabled={isPatching || isSelf}
          onCheckedChange={(checked) => applyPatch({ is_admin: checked })}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={user.records_exempt}
          disabled={isPatching}
          onCheckedChange={(checked) => applyPatch({ records_exempt: checked })}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={!user.disabled}
          disabled={isPatching || isSelf}
          onCheckedChange={(checked) => applyPatch({ disabled: !checked })}
        />
      </TableCell>
      <TableCell>
        <Input
          className="w-14 h-7 px-1.5 text-xs"
          value={builtinRank}
          placeholder="—"
          onChange={(e) => setBuiltinRank(e.target.value)}
          onBlur={commitBuiltinRank}
        />
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Input
            className="w-14 h-7 px-1.5 text-xs"
            value={collectionSlots}
            onChange={(e) => setCollectionSlots(e.target.value)}
            onBlur={commitSlots}
          />
          <Input
            className="w-14 h-7 px-1.5 text-xs"
            value={pinnedSlots}
            onChange={(e) => setPinnedSlots(e.target.value)}
            onBlur={commitSlots}
          />
        </div>
      </TableCell>
      <TableCell>
        <AdminPasswordDialog
          mode={passwordDialog}
          userID={user.id}
          onOpenChange={(open) => !open && setPasswordDialog(null)}
          onError={onError}
        />
        <AdminUserDeleteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          user={user}
          onError={onError}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {user.username ? (
              <DropdownMenuItem onClick={() => setPasswordDialog("reset")}>
                Reset password
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => setPasswordDialog("create")}>
                Add password login
              </DropdownMenuItem>
            )}
            {user.username && (
              <DropdownMenuItem onClick={handleRemoveCredentials}>
                Remove password login
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              disabled={isSelf}
            >
              Delete account
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
